<script
  setup
  lang="ts"
>
import {
  apps,
  formatting,
  LegacyInvoice,
  legacyInvoiceKey,
  LegacyOrder,
  legacyOrderKey,
  useLocalization,
  usePhrases
} from '@hantera/portal-app';
import {ContextMenuButton, Panel} from '@hantera/design-system';
import {computed, inject, provide, ref} from 'vue';
import AppSlotView from '@/AppSlotView.vue';
import InvoiceRow from './InvoiceRow.vue';
import Decimal from 'decimal.js';

const props = defineProps<{
  invoice: LegacyInvoice
}>();

provide(legacyInvoiceKey, props.invoice);

const order = inject(legacyOrderKey, ref<LegacyOrder>({} as any));
const phrase = usePhrases('invoice');

const t = useLocalization();
const { date, currency } = formatting;

const state = computed(() => {
  if (props.invoice.isCancelled) {
    return 'Cancelled';
  }

  const totalCaptured = props.invoice.captures.filter(r => r.isSuccessful).reduce((res, c) => res.add(c.capturedAmount), new Decimal(0));
  if (totalCaptured.eq(props.invoice.invoiceTotal)) {
    return 'Paid';
  }

  return 'Unpaid'
});

const invoiceNet = computed(() => {
  return props.invoice.invoiceTotal.sub(props.invoice.invoiceVat);
});

const invoiceVat = computed(() => {
  return props.invoice.invoiceVat;
});

const invoiceTotal = computed(() => {
  return props.invoice.invoiceTotal;
});

</script>

<template>
  <Panel class="invoice">

    <div class="invoice__header">
      <div
        class="invoice__state"
        :style="`background-color: var(--state-colors__invoice-${state.toLowerCase()})`"
        :title="phrase(state)"
      ></div>
      <div class="invoice__header-details">
        <div class="invoice__title">
          <div class="invoice__number">{{ invoice.invoiceNumber ?? t('orderView.invoice.newInvoice') }}</div>
          <div class="invoice__date">{{ date(invoice.createdUtc) }}</div>
          <AppSlotView :slot="apps.legacySlots.legacyOrder.invoice.header" />
        </div>
      </div>
      <ContextMenuButton class="invoice__context-menu">
        <AppSlotView :slot="apps.legacySlots.legacyOrder.invoice.contextMenu" />
      </ContextMenuButton>
    </div>

    <InvoiceRow
      v-for="row in invoice.rows"
      :key="row.referenceType + row.referenceId"
      :row="row"
    />

    <AppSlotView :slot="apps.legacySlots.legacyOrder.invoice.rows" />

    <div class="invoice__summary">
      <div class="invoice__summary-title">
        {{ phrase('Subtotal:') }}
      </div>
      <div class="invoice__summary-value">
        {{ currency(invoiceNet, order.currencyCode) }}
      </div>
      <div class="invoice__summary-title">
        {{ phrase('VAT:') }}
      </div>
      <div class="invoice__summary-value">
        {{ currency(invoiceVat, order.currencyCode) }}
      </div>
      <div class="invoice__summary-title invoice__summary-total">
        {{ phrase('Total Due (incl. VAT):') }}
      </div>
      <div class="invoice__summary-value invoice__summary-total">
        {{ currency(invoiceTotal, order.currencyCode) }}
      </div>
    </div>

    <div class="invoice__footer">
      <AppSlotView :slot="apps.legacySlots.legacyOrder.invoice.footer" />
    </div>
  </Panel>
</template>

<style>
.invoice {
  max-width: 800px;
  position: relative;
}

.invoice__header {
  position: relative;
  padding-left: 10px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.invoice__state {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-radius: 5px;
}

.invoice__header-details {
  flex-grow: 1;
}

.invoice__title {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  gap: 10px;
  align-items: center;
}

.invoice__number {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}

.invoice__date {
  white-space: nowrap;
}

.invoice__context-menu {
  height: 32px;
  width: 32px;
}

.invoice__summary {
  display: grid;
  justify-content: end;
  grid-template-columns: max-content max-content;
  row-gap: 3px;
  column-gap: 10px;
  text-align: right;
  padding-bottom: 5px;
}

.invoice__summary-total {
  font-weight: bold;
}

.invoice__footer {
  margin: 0 -5px -5px -5px;
}
</style>
